import React, { useCallback, useMemo } from "react";
import {
  ActivityIndicator,
  Platform,
  StyleProp,
  ViewStyle,
} from "react-native";
import {
  graphql,
  useFragment,
  usePaginationFragment,
  useSubscription,
} from "react-relay";

import { TeamDefaultTodoCategory_TodoCategoryTeam$key } from "./__generated__/TeamDefaultTodoCategory_TodoCategoryTeam.graphql";
import {
  AHIcon,
  AHSeparator,
  AHTodoCategoryIcon,
  Box,
  Circle,
  Space,
  TouchableBox,
  useLayoutContext,
  useTheme,
  useTodoSorter,
} from "arkh";
import { TeamDefaultTodoCategoryTodoDeleteSubscription } from "./__generated__/TeamDefaultTodoCategoryTodoDeleteSubscription.graphql";
import { TeamDefaultTodoCategoryTodoUpdateSubscription } from "./__generated__/TeamDefaultTodoCategoryTodoUpdateSubscription.graphql";
import { TeamDefaultTodoCategorySubscription } from "./__generated__/TeamDefaultTodoCategorySubscription.graphql";
import { FormattedMessage } from "react-intl";
import { useSizeClass } from "../../../../common/Utils/useSizeClass";
import { SizeClass } from "@/src/common/Utils/SizeClass";

import { Image } from "expo-image";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { TeamDefaultTodoCategory_TodoCategoryTeamPager$key } from "./__generated__/TeamDefaultTodoCategory_TodoCategoryTeamPager.graphql";
import { useHotkeys } from "../../../../useHotKeys";
import { isDarkColor } from "../TeamHome/isDarkColor";

import { TodoCategorySectionHeader } from "./TodoCategorySectionHeader";
import Animated from "react-native-reanimated";
import { defaultLayoutTransition } from "@/src/defaultLayoutTransition";
import { TodoListItem } from "@/src/components/TodoListItem";

const TeamDefaultTodoCategoryMoreButtonTitle = (
  <FormattedMessage
    defaultMessage="More"
    description="TeamDefaultTodoCategory more button"
  />
);

export const TeamDefaultTodoCategory = React.memo(TeamDefaultTodoCategoryC);

function TeamDefaultTodoCategoryC(props: {
  teamId: string;
  ListHeaderComponent: React.ReactElement | null;
  contentContainerStyle?: StyleProp<ViewStyle>;
  defaultTodoCategory: TeamDefaultTodoCategory_TodoCategoryTeam$key;
  openTodo: (autofocusResponse: boolean, todoId: string) => void;
  openTodoCategory: (categoryId: string, categoryName: string) => void;
  addTodo?: (categoryid: string) => void;
  bottomSafeArea: boolean;
}) {
  const data = useFragment(
    graphql`
      fragment TeamDefaultTodoCategory_TodoCategoryTeam on TodoCategoryTeam {
        id
        notResolvedCount
        team {
          id
          color
        }
        category {
          id
          title
        }
        allTodos: todos {
          totalCount
        }
        ...TeamDefaultTodoCategory_TodoCategoryTeamPager
      }
    `,
    props.defaultTodoCategory
  );
  const {
    data: pagerData,
    hasNext,
    loadNext,
    isLoadingNext,
  } = usePaginationFragment(
    graphql`
      fragment TeamDefaultTodoCategory_TodoCategoryTeamPager on TodoCategoryTeam
      @refetchable(queryName: "TeamDefaultTodoCategoryPagerQuery") {
        id

        todos(
          first: $first
          after: $after
          status: $status
          includeNewlyCompleted: true
        ) @connection(key: "TeamDefaultTodoCategory_todos") {
          __id
          edges {
            node {
              id
              ordinal

              ...TodoListItem_todo
            }
          }
        }
      }
    `,
    data as TeamDefaultTodoCategory_TodoCategoryTeamPager$key
  );

  useSubscription<TeamDefaultTodoCategorySubscription>(
    useMemo(
      () => ({
        subscription: graphql`
          subscription TeamDefaultTodoCategorySubscription(
            $categoryId: ID!
            $teamId: ID!
            $connections: [ID!]!
          ) {
            todoCreatedNew(categoryId: $categoryId, teamIds: [$teamId]) {
              todo
                @prependNode(
                  connections: $connections
                  edgeTypeName: "TodoEdge"
                ) {
                id

                createdAtDate {
                  isoString
                }
                ordinal
                ...TodoListItem_todo
                category {
                  id
                  team(id: $teamId) {
                    id
                    notResolvedCount
                  }
                }
              }
            }
          }
        `,
        variables: {
          categoryId: data.category.id,
          teamId: props.teamId,
          connections: pagerData.todos?.__id ? [pagerData.todos.__id] : [],
        },
      }),

      [data.category.id, pagerData.todos?.__id, props.teamId]
    )
  );

  useSubscription<TeamDefaultTodoCategoryTodoUpdateSubscription>(
    useMemo(
      () => ({
        subscription: graphql`
          subscription TeamDefaultTodoCategoryTodoUpdateSubscription(
            $categoryId: ID!
            $teamId: ID!
          ) {
            todoChanged(teamIds: [$teamId], categoryId: $categoryId) {
              ordinal
              ...TodoListItem_todo
            }
          }
        `,
        variables: {
          categoryId: data.category.id,
          teamId: props.teamId,
        },
      }),

      [data.category.id, props.teamId]
    )
  );

  useSubscription<TeamDefaultTodoCategoryTodoDeleteSubscription>(
    useMemo(
      () => ({
        subscription: graphql`
          subscription TeamDefaultTodoCategoryTodoDeleteSubscription(
            $categoryId: ID!
            $teamId: ID!
          ) {
            todoDeleted(categoryIds: [$categoryId], teamIds: [$teamId]) {
              id @deleteRecord
              category {
                team(id: $teamId) {
                  id
                  notResolvedCount
                }
              }
            }
          }
        `,
        variables: {
          categoryId: data.category.id,
          teamId: props.teamId,
        },
      }),

      [data.category.id, props.teamId]
    )
  );
  const isCompact = useSizeClass().width === SizeClass.COMPACT;
  const edges = useMemo(() => {
    return pagerData.todos.edges;
  }, [pagerData.todos.edges]);

  const layout = useLayoutContext();
  const theme = useTheme();
  const todoSorter = useTodoSorter();

  const sortedEdges = useMemo(() => {
    return edges
      .filter((edge) => edge?.node)
      .sort((a, b) => {
        return todoSorter(a?.node, b?.node);
      });
  }, [edges, todoSorter]);

  const listData = useMemo(() => {
    if (sortedEdges.length === 0) {
      return [];
    }
    return ["header" as const, ...sortedEdges];
  }, [sortedEdges]);

  const spaceHorizontal = isCompact ? "none" : "medium";
  const Separator = useCallback(
    () => <AHSeparator spaceHorizontal={spaceHorizontal} />,
    [spaceHorizontal]
  );

  const isEmpty = data.allTodos.totalCount === 0;

  const empty = isEmpty && listData.length === 0;

  const ListFooterComponent = useMemo(() => {
    return empty ? (
      <TeamDefaultTodoCategoryGetStarted
        onMore={() => {
          props.openTodoCategory(data.category.id, data.category.title);
        }}
        spaceHorizontal="medium"
        title={data.category.title}
      />
    ) : (
      <TeamDefaultTodoCategoryPendingEmpty
        onMore={() => {
          props.openTodoCategory(data.category.id, data.category.title);
        }}
        spaceHorizontal="medium"
        title={data.category.title}
      />
    );
  }, [data.category.id, data.category.title, empty, props]);

  const { openTodo, openTodoCategory } = props;
  const renderItem = useCallback(
    ({ item, index }: { item: (typeof listData)[number]; index: number }) => {
      if (item === "header") {
        return (
          <TodoCategorySectionHeader
            ctaPress={() => {
              openTodoCategory(data.category.id, data.category.title);
            }}
            ctaTitle={TeamDefaultTodoCategoryMoreButtonTitle}
            notResolvedCount={data.notResolvedCount}
            spaceHorizontal={spaceHorizontal}
            title={data.category.title}
            icon={<AHTodoCategoryIcon icon="Check" />}
          />
        );
      }
      return (
        <Box key={item.node.id} spaceHorizontal={spaceHorizontal}>
          <TodoListItem
            openTodo={openTodo}
            todo={item.node}
            isLast={listData.length - 1 === index}
          />
        </Box>
      );
    },
    [
      data.category.id,
      data.category.title,
      data.notResolvedCount,
      listData.length,
      openTodo,
      openTodoCategory,
      spaceHorizontal,
    ]
  );

  const insets = useSafeAreaInsets();

  const onRequestCreate = useCallback(() => {
    props.addTodo?.(data.category.id);
  }, [data.category.id, props]);
  useHotkeys("n", onRequestCreate, {
    preventDefault: true,
  });
  const tintColor = useMemo(
    () => data.team?.color ?? theme.primary,
    [data.team?.color, theme.primary]
  );

  const isSticky = listData.length > 0 ? [1] : undefined;

  const isStickyKey =
    Platform.OS === "android"
      ? isSticky
        ? "sticky"
        : "not-sticky"
      : undefined;
  return (
    <>
      <Animated.FlatList
        key={isStickyKey} //https://github.com/facebook/react-native/issues/25157
        style={{ flex: 1 }}
        scrollIndicatorInsets={{
          bottom: insets.bottom,
        }}
        contentContainerStyle={{
          paddingBottom: insets.bottom + layout.space.xxlarge,
        }}
        data={listData}
        keyExtractor={(item) => (item === "header" ? "header" : item.node.id)}
        ItemSeparatorComponent={Separator}
        stickyHeaderIndices={isSticky ? [1] : undefined}
        ListHeaderComponent={props.ListHeaderComponent}
        ListEmptyComponent={ListFooterComponent}
        renderItem={renderItem}
        onEndReached={() => {
          if (hasNext && !isLoadingNext) {
            loadNext(25);
          }
        }}
        ListFooterComponent={
          isLoadingNext ? (
            <Box spaceTop="medium">
              <ActivityIndicator size="large" />
            </Box>
          ) : null
        }
        itemLayoutAnimation={defaultLayoutTransition}
      />
      {props.addTodo ? (
        <TouchableBox
          onPress={onRequestCreate}
          style={{
            position: "absolute",
            bottom:
              layout.space.medium + (props.bottomSafeArea ? insets.bottom : 0),
            right: layout.space.medium,
          }}
        >
          <Circle
            size="large"
            style={{
              backgroundColor: tintColor,
            }}
          >
            <AHIcon
              type="add"
              color={isDarkColor(tintColor) ? "text-inverted" : "text-primary"}
              forceSize={20}
            />
          </Circle>
        </TouchableBox>
      ) : null}
    </>
  );
}

function TeamDefaultTodoCategoryGetStarted(props: {
  title: string;
  spaceHorizontal: Space;
  onMore: () => void;
}) {
  return (
    <>
      <TodoCategorySectionHeader
        title={props.title}
        spaceHorizontal={props.spaceHorizontal}
        ctaPress={props.onMore}
        ctaTitle={
          <FormattedMessage
            defaultMessage="Get started"
            description="TeamDefaultTodoCategory more button"
          />
        }
        icon={<AHTodoCategoryIcon icon="Check" />}
      />
      <Box spaceHorizontal={props.spaceHorizontal}>
        <Box
          color="surface-primary"
          space="medium"
          cornerRadiusBottomRight="large"
          cornerRadiusBottomLeft="large"
        >
          <Image
            source={require("./Happy.png")}
            style={{ height: 300 }}
            contentFit="contain"
          />
        </Box>
      </Box>
    </>
  );
}

function TeamDefaultTodoCategoryPendingEmpty(props: {
  title: string;
  spaceHorizontal: Space;
  onMore: () => void;
}) {
  return (
    <>
      <TodoCategorySectionHeader
        title={props.title}
        spaceHorizontal={props.spaceHorizontal}
        ctaPress={props.onMore}
        notResolvedCount={0}
        ctaTitle={TeamDefaultTodoCategoryMoreButtonTitle}
        icon={<AHTodoCategoryIcon icon="Check" />}
      />
      <Box spaceHorizontal={props.spaceHorizontal}>
        <Box
          color="surface-primary"
          space="medium"
          cornerRadiusBottomRight="large"
          cornerRadiusBottomLeft="large"
        >
          <Image
            source={require("./Happy.png")}
            style={{ height: 300 }}
            contentFit="contain"
          />
        </Box>
      </Box>
    </>
  );
}
